import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { Toaster } from './components/ui/sonner';

// Pages
import HomePage from './pages/HomePage';
import BrowsePage from './pages/BrowsePage';
import ListingDetailPage from './pages/ListingDetailPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import CreateListingPage from './pages/CreateListingPage';
import AdminPage from './pages/AdminPage';
import SettingsPage from './pages/SettingsPage';
import MessagesPage from './pages/MessagesPage';
import BulkUploadPage from './pages/BulkUploadPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SellerProfilePage from './pages/SellerProfilePage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';

// Protected Route Component
const ProtectedRoute = ({ children, roles = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (roles.length > 0 && !roles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Seller Route - for sellers and dealers
const SellerRoute = ({ children }) => {
  return (
    <ProtectedRoute roles={['seller', 'dealer', 'admin']}>
      {children}
    </ProtectedRoute>
  );
};

// Admin Route
const AdminRoute = ({ children }) => {
  return (
    <ProtectedRoute roles={['admin']}>
      {children}
    </ProtectedRoute>
  );
};

// Layout Component
const Layout = ({ children, showFooter = true }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      {showFooter && <Footer />}
    </div>
  );
};

// Auth Layout (no header/footer)
const AuthLayout = ({ children }) => {
  return <div className="min-h-screen">{children}</div>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          <Layout>
            <HomePage />
          </Layout>
        }
      />
      <Route
        path="/browse"
        element={
          <Layout>
            <BrowsePage />
          </Layout>
        }
      />
      <Route
        path="/listings/:id"
        element={
          <Layout>
            <ListingDetailPage />
          </Layout>
        }
      />
      <Route
        path="/sellers/:id"
        element={
          <Layout>
            <SellerProfilePage />
          </Layout>
        }
      />
      <Route
        path="/terms"
        element={
          <Layout>
            <TermsOfServicePage />
          </Layout>
        }
      />
      
      {/* Auth Routes */}
      <Route
        path="/login"
        element={
          <AuthLayout>
            <LoginPage />
          </AuthLayout>
        }
      />
      <Route
        path="/register"
        element={
          <AuthLayout>
            <RegisterPage />
          </AuthLayout>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <AuthLayout>
            <ForgotPasswordPage />
          </AuthLayout>
        }
      />
      <Route
        path="/reset-password"
        element={
          <AuthLayout>
            <ResetPasswordPage />
          </AuthLayout>
        }
      />
      
      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/saved"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Layout showFooter={false}>
              <MessagesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout showFooter={false}>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <Layout showFooter={false}>
              <PaymentSuccessPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Seller Routes */}
      <Route
        path="/listings/create"
        element={
          <SellerRoute>
            <Layout showFooter={false}>
              <CreateListingPage />
            </Layout>
          </SellerRoute>
        }
      />
      <Route
        path="/listings/:id/edit"
        element={
          <SellerRoute>
            <Layout showFooter={false}>
              <CreateListingPage />
            </Layout>
          </SellerRoute>
        }
      />
      <Route
        path="/bulk-upload"
        element={
          <ProtectedRoute roles={['dealer', 'admin']}>
            <Layout showFooter={false}>
              <BulkUploadPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <Layout>
              <AdminPage />
            </Layout>
          </AdminRoute>
        }
      />
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
